import { ORGANISATION_ROLE_MESSAGE } from '@/components/modal/shared-signup.messages';
import { PhoneInput } from '@/components/ui/phone-input';
import { useTrackSignupPhoneFieldViewed } from '@/hooks/useTrackSignupPhoneFieldViewed';
import { type ClinicianTypeInput } from '@/lib/types';
import { getPhoneNumberVariantForCountry } from '@/utils/country-config';
import { Button } from '@heidi/design-system/button';
import {
  TypographyCaption,
  TypographyH2,
  TypographyP,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { FieldType } from '@heidi/runtime/analytics-vocabulary';
import { type LanguageCode, OrganisationRole } from '@heidi/schema/ml-scribe';
import { countries, type CountryType } from '@heidi/utils/countries';
import { getNativeDisplayLanguageName } from '@heidi/utils/i18n';
import { AlertCircle } from 'lucide-react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { type Country, parsePhoneNumber } from 'react-phone-number-input';

import { useOnboardingFieldTracking } from '@/models/analytics/features/useOnboardingFieldTracking';
import { roleStepMessages, subdivisionLabelMessages } from '@heidi/growth';
import { ResponsiveSelect } from '../shared';
import { OnboardingClinicianTypeSelect } from '../shared/dropdowns/OnboardingClinicianTypeSelect';
import { OnboardingCountrySelect } from '../shared/dropdowns/OnboardingCountrySelect';
import { OnboardingJurisdictionSelect } from '../shared/dropdowns/OnboardingJurisdictionSelect';
import { getSubdivisionLabelDescriptor } from './country-subdivision-labels';

interface RoleStepProps {
  specialty: ClinicianTypeInput | null;
  otherSpecialty: string;
  role: OrganisationRole | null;
  country: CountryType | null;
  jurisdiction: string;
  displayLanguage: LanguageCode | null;
  availableDisplayLanguages: LanguageCode[];
  showLanguageDropdown: boolean;
  phoneNumber: string;
  onSpecialtyChange: (value: ClinicianTypeInput | null) => void;
  onOtherSpecialtyChange: (value: string) => void;
  onRoleChange: (value: OrganisationRole | null) => void;
  onCountryChange: (value: CountryType | null) => void;
  onJurisdictionChange: (value: string) => void;
  onDisplayLanguageChange: (value: LanguageCode) => void;
  onPhoneNumberChange: (value: string) => void;
  onContinue: () => void;
  isValid: boolean;
}

export function RoleStep({
  specialty,
  otherSpecialty,
  role,
  country,
  jurisdiction,
  displayLanguage,
  availableDisplayLanguages,
  showLanguageDropdown,
  phoneNumber,
  onSpecialtyChange,
  onOtherSpecialtyChange,
  onRoleChange,
  onCountryChange,
  onJurisdictionChange,
  onDisplayLanguageChange,
  onPhoneNumberChange,
  onContinue,
  isValid,
}: RoleStepProps) {
  const intl = useIntl();
  const { trackImmediate } = useOnboardingFieldTracking('role');
  const subdivisionLabelDescriptor = getSubdivisionLabelDescriptor(country?.name);
  const subdivisionLabel = intl.formatMessage(
    subdivisionLabelDescriptor ?? subdivisionLabelMessages.state
  );

  const phoneNumberVariant = getPhoneNumberVariantForCountry(country?.countryCode);
  const showPhoneNumber = phoneNumberVariant !== 'hidden';
  const isPhoneNumberOptional = phoneNumberVariant === 'optional';

  useTrackSignupPhoneFieldViewed({
    isVisible: showPhoneNumber,
    isOptional: isPhoneNumberOptional,
    surface: 'onboarding_role_step',
    countryCode: country?.countryCode,
  });

  const { trackTextDebounced } = useOnboardingFieldTracking('role');

  const allowedCountries = useMemo(
    () => countries.filter((c) => !!c.countryCode).map((c) => c.countryCode as Country),
    []
  );

  const roleOptions = useMemo(
    () =>
      Object.values(OrganisationRole).map((roleValue) => ({
        value: roleValue,
        label: intl.formatMessage(ORGANISATION_ROLE_MESSAGE[roleValue]),
      })),
    [intl]
  );

  const displayLanguageOptions = useMemo(
    () =>
      availableDisplayLanguages.map((langCode) => ({
        value: langCode,
        label: getNativeDisplayLanguageName(langCode),
      })),
    [availableDisplayLanguages]
  );

  const hasCountryPhoneMismatch = useMemo(() => {
    if (!country || !phoneNumber || !showPhoneNumber) return false;
    try {
      const parsed = parsePhoneNumber(phoneNumber);
      if (parsed?.country) {
        return parsed.country !== country.countryCode;
      }
    } catch {
      return false;
    }
    return false;
  }, [country, phoneNumber, showPhoneNumber]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isValid) {
      onContinue();
    }
  };

  const specialtyField = (
    <div className="flex flex-col gap-2">
      <TypographyP2>{intl.formatMessage(roleStepMessages.specialty)}</TypographyP2>
      <OnboardingClinicianTypeSelect
        selectedSpecialty={specialty ?? undefined}
        setSelectedSpecialty={(value) => {
          // Capture the chosen specialty (bounded, non-PII clinician-type
          // name) as `field_value` on the field-selected event.
          if (value) trackImmediate('specialty', FieldType.Dropdown, value.name);
          onSpecialtyChange(value ?? null);
        }}
        otherSpecialty={otherSpecialty}
        setOtherSpecialty={(value) => onOtherSpecialtyChange(value ?? '')}
        className="[&_button[data-state=open]]:border-border-strong [&_button]:rounded-md [&_button]:bg-surface [&_button]:hover:bg-surface-hover"
        drawerTitle={intl.formatMessage(roleStepMessages.specialty)}
        countryCode={country?.countryCode}
        disabled={!country}
      />
    </div>
  );

  const roleField = (
    <div className="flex flex-col gap-1.5">
      <TypographyP2>{intl.formatMessage(roleStepMessages.role)}</TypographyP2>
      <ResponsiveSelect
        id="role"
        value={role ?? ''}
        onValueChange={(value) => onRoleChange(value as OrganisationRole)}
        options={roleOptions}
        placeholder={intl.formatMessage(roleStepMessages.selectRole)}
        drawerTitle={intl.formatMessage(roleStepMessages.role)}
        className="bg-surface hover:bg-surface-hover data-[state=open]:border-border-strong"
      />
    </div>
  );

  const countryField = (
    <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:gap-6">
      <div className="flex min-w-0 flex-col gap-1.5">
        <TypographyP2Bold>
          {intl.formatMessage(roleStepMessages.country)}
        </TypographyP2Bold>
        <OnboardingCountrySelect
          value={country ?? undefined}
          setValue={onCountryChange}
          className="[&_button[aria-expanded=true]]:border-border-strong [&_button]:rounded-md [&_button]:bg-surface [&_button]:hover:bg-surface-hover"
          popoverClassName="min-w-64"
          drawerTitle={intl.formatMessage(roleStepMessages.country)}
        />
      </div>

      <div className="flex min-w-0 flex-col gap-1.5">
        <TypographyP2Bold>{subdivisionLabel}</TypographyP2Bold>
        <OnboardingJurisdictionSelect
          countryName={country?.name}
          value={jurisdiction || null}
          onChange={onJurisdictionChange}
          className="rounded-md bg-surface hover:bg-surface-hover data-[state=open]:border-border-strong"
          subdivisionLabel={subdivisionLabel}
          drawerTitle={subdivisionLabel}
        />
      </div>
    </div>
  );

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col gap-6 lg:gap-10">
      <div className="flex flex-col gap-4">
        <TypographyH2>
          {intl.formatMessage(roleStepMessages.tellUsAboutYourRole)}
        </TypographyH2>
        <TypographyP className="hidden text-text-primary lg:block">
          {intl.formatMessage(roleStepMessages.roleSubtitle)}
        </TypographyP>
      </div>

      <div className="flex flex-col gap-4 lg:gap-6">
        {countryField}
        {roleField}
        {specialtyField}

        {showLanguageDropdown && (
          <div className="flex flex-col gap-1.5">
            <TypographyP2>
              {intl.formatMessage(roleStepMessages.displayLanguage)}
            </TypographyP2>
            <ResponsiveSelect
              id="displayLanguage"
              value={displayLanguage ?? ''}
              onValueChange={(value) => onDisplayLanguageChange(value as LanguageCode)}
              options={displayLanguageOptions}
              placeholder={intl.formatMessage(roleStepMessages.selectDisplayLanguage)}
              drawerTitle={intl.formatMessage(roleStepMessages.displayLanguage)}
              className="bg-surface hover:bg-surface-hover data-[state=open]:border-border-strong"
            />
          </div>
        )}

        {showPhoneNumber && (
          <div className="flex flex-col gap-1.5">
            {isPhoneNumberOptional ? (
              <TypographyP2Bold className="leading-tight">
                {intl.formatMessage(roleStepMessages.phoneNumberOptional)}
              </TypographyP2Bold>
            ) : (
              <div className="flex gap-0.5">
                <TypographyP2Bold className="leading-tight">
                  {intl.formatMessage(roleStepMessages.phoneNumber)}
                </TypographyP2Bold>
                <TypographyP2Bold className="text-validation-destructive">
                  *
                </TypographyP2Bold>
              </div>
            )}
            <PhoneInput
              value={phoneNumber}
              onChange={(value) => {
                // Debounced field_selected — one event per pause, not per keystroke.
                trackTextDebounced('phone_number');
                onPhoneNumberChange(value ?? '');
              }}
              allowedCountries={allowedCountries}
              defaultCountry={country?.countryCode as Country}
              placeholder={intl.formatMessage(roleStepMessages.phoneNumberPlaceholder)}
              className="[&>div]:hover:bg-surface-hover [&_button[data-state=open]]:border-border-strong [&_button]:bg-surface [&_button]:hover:bg-surface-hover"
            />
            {hasCountryPhoneMismatch && (
              <div className="flex items-start gap-2 rounded-md bg-validation-warning-muted p-2">
                <AlertCircle className="mt-0.5 size-4 shrink-0 text-validation-warning" />
                <TypographyCaption className="text-validation-warning">
                  {intl.formatMessage(roleStepMessages.countryPhoneMismatchWarning)}
                </TypographyCaption>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex flex-1 items-end pt-4">
        <Button size="lg" type="submit" disabled={!isValid} className="w-full">
          {intl.formatMessage(roleStepMessages.continue)}
        </Button>
      </div>
    </form>
  );
}
